import type {
	CrmFilterRule,
	MarketingFilterMetadataResponse,
	SavedSegmentListResponse,
	SegmentPreviewRequest,
	SegmentPreviewResponse,
} from "@/shared/api/generated/types/marketing"
import {
	estimateCountAfterExclusions,
	normalizeSegmentExclusion,
} from "@/widgets/marketing-segment-builder/model/segment-exclusion-helpers"

export const marketingFilterMetadataFixture: MarketingFilterMetadataResponse = {
	rules: [
		{
			name: "client_birthday",
			label: "День рождения",
			type: "string",
			operators: ["birthday_eq", "today", "lt"],
		},
		{
			name: "client_last_visit_days",
			label: "Дней с последнего визита",
			type: "string",
			operators: ["lte", "gte", "range"],
		},
		{
			name: "client_sex",
			label: "Пол",
			type: "ddl",
			operators: ["eq"],
			enumValues: ["MALE", "FEMALE", "NOT_SET"],
		},
		{
			name: "client_age",
			label: "Возраст",
			type: "number",
			operators: ["lt", "eq", "gt", "range"],
		},
		{
			name: "client_use_service",
			label: "Услуга",
			type: "service",
			operators: ["eq"],
		},
		{
			name: "client_use_business",
			label: "Филиал",
			type: "business",
			operators: ["eq"],
		},
		{
			name: "client_use_specialization",
			label: "Специализация",
			type: "specialization",
			operators: ["eq"],
		},
		{
			name: "client_use_resource",
			label: "Врач",
			type: "resource",
			operators: ["eq"],
		},
		{
			name: "client_phone",
			label: "Телефон",
			type: "phone",
			operators: ["contains"],
		},
		{
			name: "client_name",
			label: "ФИО",
			type: "fio",
			operators: ["eq"],
		},
		{
			name: "client_email",
			label: "Email",
			type: "string",
			operators: ["eq", "contains"],
		},
		{
			name: "client_money_spent",
			label: "Сумма покупок",
			type: "number",
			operators: ["lte", "gte", "range"],
		},
		{
			name: "client_avg_payment",
			label: "Средний чек",
			type: "number",
			operators: ["lte", "gte", "range"],
		},
	],
	popularPresets: [
		{
			id: "birthday-today",
			label: "День рождения сегодня",
			rules: [{ name: "client_birthday", operator: "today", value: "" }],
		},
		{
			id: "inactive-90",
			label: "Не были 90+ дней",
			rules: [{ name: "client_last_visit_days", operator: "gte", value: "90" }],
		},
		{
			id: "inactive-180",
			label: "Не были 6+ месяцев",
			rules: [
				{ name: "client_last_visit_days", operator: "gte", value: "180" },
			],
		},
		{
			id: "female",
			label: "Женщины",
			rules: [{ name: "client_sex", operator: "eq", value: "FEMALE" }],
		},
	],
	branches: [
		{ id: "branch-main", name: "Основной филиал" },
		{ id: "branch-north", name: "Северный филиал" },
	],
	services: [
		{ id: "svc-consult", name: "Консультация терапевта" },
		{ id: "svc-checkup", name: "Чек-ап" },
	],
	specializations: [
		{ id: "spec-therapy", name: "Терапия" },
		{ id: "spec-cardio", name: "Кардиология" },
		{ id: "spec-pediatric", name: "Педиатрия" },
	],
	resources: [
		{
			id: "res-ivanov",
			name: "Иванов И.П.",
			specializationId: "spec-therapy",
			specializationName: "Терапия",
		},
		{
			id: "res-petrova",
			name: "Петрова А.С.",
			specializationId: "spec-cardio",
			specializationName: "Кардиология",
		},
		{
			id: "res-sidorenko",
			name: "Сидоренко М.В.",
			specializationId: "spec-pediatric",
			specializationName: "Педиатрия",
		},
	],
}

export const marketingSavedSegmentsFixture: SavedSegmentListResponse = {
	data: [
		{
			id: "seg-vip",
			name: "VIP пациенты",
			rules: [{ name: "client_money_spent", operator: "gte", value: "50000" }],
		},
		{
			id: "seg-pediatric",
			name: "Дети до 12 лет",
			rules: [{ name: "client_age", operator: "lt", value: "12" }],
		},
	],
	paging: { page: 1, pageSize: 20, total: 2 },
}

const PREVIEW_BASE_TOTAL = 7696
const PREVIEW_LIMIT = 5000

export function computeSegmentPreviewFixture(
	request: SegmentPreviewRequest,
): SegmentPreviewResponse {
	const ruleCount = request.rules.length
	const factor = ruleCount === 0 ? 1 : Math.max(0.12, 1 - ruleCount * 0.18)
	let count = Math.round(PREVIEW_BASE_TOTAL * factor)
	const countBeforeExclusions = count

	const exclusion = normalizeSegmentExclusion(request.exclusion)
	if (exclusion.enabled) {
		count = estimateCountAfterExclusions(count, exclusion)
	}

	const percent = Math.round((count / PREVIEW_BASE_TOTAL) * 1000) / 10

	return {
		count,
		countBeforeExclusions,
		totalInBase: PREVIEW_BASE_TOTAL,
		percent,
		overLimit: count > PREVIEW_LIMIT,
		limit: PREVIEW_LIMIT,
	}
}

export function formatRulesForConditions(rules: CrmFilterRule[]): string {
	return rules
		.map((rule) => {
			const meta = marketingFilterMetadataFixture.rules.find(
				(item) => item.name === rule.name,
			)
			const label = meta?.label ?? rule.name
			return `${label} (${rule.operator})`
		})
		.join(", ")
}
